// Will likely be unused when ad account linking flows are redesigned
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Icon,
} from '@makeably/creativex-design-system';
import styles from './Tree.module.css';

const treeDataProps = {
  children: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  key: PropTypes.string,
  title: PropTypes.string,
};

const propTypes = {
  defaultCheckedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCheck: PropTypes.func.isRequired,
  treeData: PropTypes.arrayOf(PropTypes.shape(treeDataProps)).isRequired,
};

function mapToCheckedKeysArray(checkedKeysMap) {
  return Array.from(checkedKeysMap.entries()).map(([key, title]) => ({
    key,
    title,
  }));
}

function Tree({
  defaultCheckedKeys: disabledAdAccountKeys,
  onCheck,
  treeData,
}) {
  const [checkedKeys, setCheckedKeys] = useState(new Map());
  const [expandedParents, setExpandedParents] = useState([]);

  const handleParentCheck = (isChecked, node) => {
    const newCheckedKeys = new Map(checkedKeys);
    if (isChecked) {
      node.children.forEach((child) => {
        newCheckedKeys.delete(child.key);
      });
      setCheckedKeys(newCheckedKeys);
      setExpandedParents((prev) => prev.filter((key) => key !== node.key));
    } else {
      node.children.forEach((child) => {
        newCheckedKeys.set(child.key, child.title);
      });
      setCheckedKeys(newCheckedKeys);
      setExpandedParents((prev) => [...prev, node.key]);
    }

    onCheck(mapToCheckedKeysArray(newCheckedKeys));
  };

  const handleChildCheck = (isChecked, node) => {
    const newCheckedKeys = new Map(checkedKeys);
    if (isChecked) {
      newCheckedKeys.delete(node.key);
    } else {
      newCheckedKeys.set(node.key, node.title);
    }
    setCheckedKeys(newCheckedKeys);

    onCheck(mapToCheckedKeysArray(newCheckedKeys));
  };

  const handleExpandToggle = (nodeKey) => {
    setExpandedParents((prev) => {
      if (prev.includes(nodeKey)) {
        return prev.filter((key) => key !== nodeKey);
      }
      return [...prev, nodeKey];
    });
  };

  const renderParent = (node) => {
    const isChecked = node.children.every((child) => checkedKeys.has(child.key));
    const isExpanded = expandedParents.includes(node.key);

    return (
      <div key={node.key} className={styles.parentContainer}>
        <div
          className={styles.clickableIcon}
          role="button"
          tabIndex={0}
          onClick={() => handleExpandToggle(node.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleExpandToggle(node.key);
            }
          }}
        >
          <Icon name={isExpanded ? 'chevronDown' : 'chevronRight'} />
        </div>
        <Checkbox
          checked={isChecked}
          label={node.title}
          onChange={() => handleParentCheck(isChecked, node)}
        />
      </div>
    );
  };

  const renderChild = (node) => {
    const isChecked = checkedKeys.has(node.key);
    const isDisabled = disabledAdAccountKeys.includes(node.key);

    return (
      <div key={node.key}>
        <Checkbox
          checked={isChecked}
          disabled={isDisabled}
          label={node.title}
          onChange={() => handleChildCheck(isChecked, node)}
        />
      </div>
    );
  };

  const renderChildren = (node) => {
    const isVisible = expandedParents.includes(node.key);
    if (!node.children || node.children.length === 0 || !isVisible) return null;

    return (
      <div className="u-flexColumn u-marginLeft-24">
        { node.children.map((child) => renderChild(child)) }
      </div>
    );
  };

  return (
    <div className="u-marginTop-8 u-marginLeft-24">
      <div className={styles.treeContainer}>
        { treeData.map((node) => (
          <div key={node.key}>
            { renderParent(node) }
            { renderChildren(node) }
          </div>
        )) }
      </div>
    </div>
  );
}

Tree.propTypes = propTypes;

export default Tree;
